import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Heading,
  SimpleGrid,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Image,
  IconButton,
  VStack,
  HStack,
  Text,
  Center,
  Spinner,
  Switch,
  Badge,
  useColorModeValue,
  Textarea,
} from '@chakra-ui/react';
import { FaPlus, FaEdit, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { hero_images_url } from '../utils/constants';
import { getSecureUrl } from '../utils/helpers';
import { SidebarWithHeader } from '../components';

const HeroImagesPage = () => {
  const [heroImages, setHeroImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    image: '',
    title: '',
    titleAr: '',
    subtitle: '',
    subtitleAr: '',
    isActive: true,
  });
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cardBg = useColorModeValue('white', 'gray.700');

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(`${hero_images_url}/admin`, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
        },
      });
      const imgs = (response.data.data || []).map((h) => ({
        ...h,
        image: h.image ? { ...h.image, url: getSecureUrl(h.image.url) } : h.image,
      }));
      setHeroImages(imgs);
    } catch (error) {
      toast({
        position: 'top',
        description: 'Error fetching hero images',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHeroImages();
    // eslint-disable-next-line
  }, []);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/jpeg, image/png',
    multiple: false,
  });

  const handleOpenModal = (heroImage = null) => {
    if (heroImage) {
      setEditingImage(heroImage);
      setFormData({
        image: heroImage.image.url,
        title: heroImage.title || '',
        titleAr: heroImage.titleAr || '',
        subtitle: heroImage.subtitle || '',
        subtitleAr: heroImage.subtitleAr || '',
        isActive: heroImage.isActive,
      });
    } else {
      setEditingImage(null);
      setFormData({
        image: '',
        title: '',
        titleAr: '',
        subtitle: '',
        subtitleAr: '',
        isActive: true,
      });
    }
    onOpen();
  };

  const handleCloseModal = () => {
    setEditingImage(null);
    setFormData({
      image: '',
      title: '',
      titleAr: '',
      subtitle: '',
      subtitleAr: '',
      isActive: true,
    });
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.image) {
      return toast({
        position: 'top',
        description: 'Please provide an image',
        status: 'error',
        duration: 5000,
      });
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        image: formData.image.startsWith('data:') ? formData.image : undefined,
      };

      if (editingImage) {
        await axios.put(`${hero_images_url}/admin/${editingImage._id}`, payload, {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
          },
        });
        toast({
          position: 'top',
          description: 'Hero image updated successfully',
          status: 'success',
          duration: 5000,
        });
      } else {
        await axios.post(`${hero_images_url}/admin`, { ...formData }, {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
          },
        });
        toast({
          position: 'top',
          description: 'Hero image created successfully',
          status: 'success',
          duration: 5000,
        });
      }
      fetchHeroImages();
      handleCloseModal();
    } catch (error) {
      toast({
        position: 'top',
        description: error.response?.data?.message || 'Error saving hero image',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hero image?')) return;

    try {
      await axios.delete(`${hero_images_url}/admin/${id}`, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
        },
      });
      toast({
        position: 'top',
        description: 'Hero image deleted successfully',
        status: 'success',
        duration: 5000,
      });
      fetchHeroImages();
    } catch (error) {
      toast({
        position: 'top',
        description: error.response?.data?.message || 'Error deleting hero image',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleToggleActive = async (heroImage) => {
    try {
      await axios.put(
        `${hero_images_url}/admin/${heroImage._id}`,
        { isActive: !heroImage.isActive },
        {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
          },
        }
      );
      fetchHeroImages();
    } catch (error) {
      toast({
        position: 'top',
        description: 'Error updating hero image',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleReorder = async (index, direction) => {
    const newImages = [...heroImages];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (swapIndex < 0 || swapIndex >= newImages.length) return;

    [newImages[index], newImages[swapIndex]] = [newImages[swapIndex], newImages[index]];
    
    const orderedIds = newImages.map((img) => img._id);

    try {
      await axios.put(
        `${hero_images_url}/admin/reorder`,
        { orderedIds },
        {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('acces_token'),
          },
        }
      );
      fetchHeroImages();
    } catch (error) {
      toast({
        position: 'top',
        description: 'Error reordering images',
        status: 'error',
        duration: 5000,
      });
    }
  };

  if (loading) {
    return (
      <SidebarWithHeader>
        <Center h="400px">
          <Spinner size="xl" color="brown.500" />
        </Center>
      </SidebarWithHeader>
    );
  }

  return (
    <SidebarWithHeader>
      <Box p={4}>
        <HStack justify="space-between" mb={6}>
          <VStack align="start" spacing={1}>
            <Heading size="lg">Hero Carousel Images</Heading>
            <Text color="gray.500" fontSize="sm">
              Manage the images that appear in the homepage hero carousel
            </Text>
          </VStack>
          <Button colorScheme="brown" leftIcon={<FaPlus />} onClick={() => handleOpenModal()}>
            Add Image
          </Button>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {heroImages.map((heroImage, index) => (
            <Box
              key={heroImage._id}
              bg={cardBg}
              borderRadius="lg"
              overflow="hidden"
              boxShadow="md"
              position="relative"
              opacity={heroImage.isActive ? 1 : 0.6}
            >
              <Box position="absolute" top={2} right={2} zIndex={1}>
                <Badge colorScheme={heroImage.isActive ? 'green' : 'red'}>
                  {heroImage.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Box>
              <Image
                src={heroImage.image.url}
                alt={heroImage.title || 'Hero image'}
                h="180px"
                w="100%"
                objectFit="cover"
              />
              <Box p={4}>
                <Text fontWeight="bold" noOfLines={1}>
                  {heroImage.title || 'No title'}
                </Text>
                <Text fontSize="sm" color="gray.500" noOfLines={1}>
                  {heroImage.subtitle || 'No subtitle'}
                </Text>
                <HStack mt={3} spacing={2} flexWrap="wrap">
                  <IconButton
                    size="sm"
                    colorScheme="gray"
                    icon={<FaArrowUp />}
                    onClick={() => handleReorder(index, 'up')}
                    isDisabled={index === 0}
                    aria-label="Move up"
                  />
                  <IconButton
                    size="sm"
                    colorScheme="gray"
                    icon={<FaArrowDown />}
                    onClick={() => handleReorder(index, 'down')}
                    isDisabled={index === heroImages.length - 1}
                    aria-label="Move down"
                  />
                  <IconButton
                    size="sm"
                    colorScheme="blue"
                    icon={<FaEdit />}
                    onClick={() => handleOpenModal(heroImage)}
                    aria-label="Edit"
                  />
                  <IconButton
                    size="sm"
                    colorScheme="red"
                    icon={<FaTrash />}
                    onClick={() => handleDelete(heroImage._id)}
                    aria-label="Delete"
                  />
                  <Switch
                    colorScheme="green"
                    isChecked={heroImage.isActive}
                    onChange={() => handleToggleActive(heroImage)}
                    ml="auto"
                  />
                </HStack>
              </Box>
            </Box>
          ))}
        </SimpleGrid>

        {heroImages.length === 0 && (
          <Center h="200px">
            <VStack>
              <Text color="gray.500">No hero images found</Text>
              <Button colorScheme="brown" onClick={() => handleOpenModal()}>
                Add your first hero image
              </Button>
            </VStack>
          </Center>
        )}

        {/* Add/Edit Hero Image Modal */}
        <Modal isOpen={isOpen} onClose={handleCloseModal} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{editingImage ? 'Edit Hero Image' : 'Add New Hero Image'}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Image</FormLabel>
                  <Center
                    bg="brown.50"
                    minHeight={200}
                    borderWidth={3}
                    borderColor="brown.200"
                    borderStyle="dashed"
                    borderRadius="lg"
                    cursor="pointer"
                    {...getRootProps()}
                  >
                    {formData.image ? (
                      <Image
                        src={formData.image}
                        alt="Hero"
                        maxH="190px"
                        objectFit="contain"
                      />
                    ) : isDragActive ? (
                      <Text>Drop the image here</Text>
                    ) : (
                      <VStack spacing={1} p={4}>
                        <Text textAlign="center">
                          Drag & drop an image here, or click to select
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          (Recommended: 1920x750 pixels)
                        </Text>
                      </VStack>
                    )}
                  </Center>
                  <Input {...getInputProps()} />
                </FormControl>

                <SimpleGrid columns={2} spacing={4} w="100%">
                  <FormControl>
                    <FormLabel>Title (English)</FormLabel>
                    <Input
                      name="title"
                      placeholder="Hero title"
                      value={formData.title}
                      onChange={handleInputChange}
                      focusBorderColor="brown.500"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Title (Arabic)</FormLabel>
                    <Input
                      name="titleAr"
                      placeholder="العنوان"
                      value={formData.titleAr}
                      onChange={handleInputChange}
                      focusBorderColor="brown.500"
                      dir="rtl"
                    />
                  </FormControl>
                </SimpleGrid>

                <SimpleGrid columns={2} spacing={4} w="100%">
                  <FormControl>
                    <FormLabel>Subtitle (English)</FormLabel>
                    <Textarea
                      name="subtitle"
                      placeholder="Hero subtitle"
                      value={formData.subtitle}
                      onChange={handleInputChange}
                      focusBorderColor="brown.500"
                      rows={2}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Subtitle (Arabic)</FormLabel>
                    <Textarea
                      name="subtitleAr"
                      placeholder="الوصف"
                      value={formData.subtitleAr}
                      onChange={handleInputChange}
                      focusBorderColor="brown.500"
                      dir="rtl"
                      rows={2}
                    />
                  </FormControl>
                </SimpleGrid>

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Active</FormLabel>
                  <Switch
                    name="isActive"
                    colorScheme="green"
                    isChecked={formData.isActive}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                colorScheme="brown"
                onClick={handleSubmit}
                isLoading={saving}
                loadingText="Saving..."
              >
                {editingImage ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </SidebarWithHeader>
  );
};

export default HeroImagesPage;
